import { useRef } from 'react'
import type { ReactNode } from 'react'
import { useRafCallback } from '@/hooks/use-raf-callback'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface MagneticButtonProps {
  children: ReactNode
  href: string
  className?: string
  strength?: number
  onNavigate?: ((href: string) => void) | undefined
}

export function MagneticButton({
  children,
  href,
  className = '',
  strength = 0.35,
  onNavigate,
}: MagneticButtonProps) {
  const ref = useRef<HTMLAnchorElement>(null)
  const reduced = useReducedMotion()

  const apply = useRafCallback((x: number, y: number) => {
    const element = ref.current
    if (element === null) return
    element.style.setProperty('--magnet-x', `${x.toFixed(2)}px`)
    element.style.setProperty('--magnet-y', `${y.toFixed(2)}px`)
  })

  const onPointerMove = (event: React.PointerEvent<HTMLAnchorElement>) => {
    if (reduced || event.pointerType !== 'mouse') return
    const element = ref.current
    if (element === null) return
    const rect = element.getBoundingClientRect()
    const offsetX = (event.clientX - rect.left - rect.width / 2) * strength
    const offsetY = (event.clientY - rect.top - rect.height / 2) * strength
    apply(offsetX, offsetY)
  }

  const reset = () => {
    apply(0, 0)
  }

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate === undefined) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <a
      ref={ref}
      href={href}
      className={`magnetic ${className}`.trim()}
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
      onBlur={reset}
      onClick={onClick}
    >
      <span className="magnetic__label">{children}</span>
    </a>
  )
}
